import { Injectable } from '@angular/core';
import { Subject, lastValueFrom, retry } from 'rxjs';
import {
  EventCalendarResponse,
  EventModalMode,
  EventModalObjects,
  EventModalResult,
  EventResponse,
  ExtendSession,
  UpcomingEvents,
} from 'src/app/shared/models/Event.model';
import { MiniLoaderService } from 'src/app/shared/modules/mini-loader/mini-loader.service';
import { EventService } from 'src/app/shared/services/event.service';
import {
  getDurationByHours,
  getUTCDateFromOffset,
} from 'src/app/shared/time.functions';
import { SimulatorsService } from '../simulators/simulators.service';
import { SimulatorResponse } from 'src/app/shared/models/simulators.model';
import { AlertType } from 'src/app/shared/models/alert';
import {
  Transaction,
  TransactionType,
} from 'src/app/shared/models/transaction';
import { environment } from 'src/environments/environment';
import { NotificationService } from 'src/app/shared/modules/notification/notification.service';
import { EventWorkerService } from 'src/app/shared/services/worker/event-worker/event-worker.service';
import { HelperService } from 'src/app/shared/services/Helper.service';
import { MatDialog } from '@angular/material/dialog';
import { EventModalComponent } from 'src/app/components/event-modal/event-modal.component';
import { RestApiService } from 'src/app/shared/services/RestApiServices.service';
import { ToastService } from 'src/app/shared/modules/toast/toast.service';
import { RealtimeService } from 'src/app/shared/services/realtime.service';
import { EventChangeType } from '@fullswing/bay-manager-realtime';
import { TranslateService } from '@ngx-translate/core';

const urlBase = '/Event';

@Injectable({
  providedIn: 'root',
})
export class EventCommonService {
  // private _subscriptions = new Subscription();
  //public transaction$ = new BehaviorSubject<Transaction>(null as any);

  private transaction = new Subject<Transaction>();
  public transaction$ = this.transaction.asObservable();

  // private _user: UserResponse;
  // public get user(): UserResponse {
  //   return this._user;
  // }
  // public set user(v: UserResponse) {
  //   this._user = v;
  // }

  private _currentEvents: EventCalendarResponse[];
  public get currentEvents(): EventCalendarResponse[] {
    return this._currentEvents;
  }
  public set currentEvents(v: EventCalendarResponse[]) {
    this._currentEvents = v;
  }

  private _currentDateSelected: Date;
  public get currentDateSelected(): Date {
    return this._currentDateSelected;
  }
  public set currentDateSelected(v: Date) {
    this._currentDateSelected = v;
  }

  private _hasEventHardChanges: boolean;
  public get hasEventHardChanges(): boolean {
    return this._hasEventHardChanges;
  }
  public set hasEventHardChanges(v: boolean) {
    this._hasEventHardChanges = v;
  }

  private pendingChanges = { created: 0, updated: 0, removed: 0 };
  private updatedToastTimer: any;

  constructor(
    private loaderService: MiniLoaderService,
    private eventService: EventService,
    private simulatorService: SimulatorsService,
    private realtimeService: RealtimeService,
    public notificatrionService: NotificationService,
    private eventWorkerService: EventWorkerService,
    private helper: HelperService,
    public dialog: MatDialog,
    private restApiService: RestApiService,
    private toastService: ToastService,
    private translate: TranslateService,
  ) {
    this._hasEventHardChanges = false;
    this._currentDateSelected = this.eventService.today;
    // this.user = new UserResponse({});
    this._currentEvents = new Array<EventCalendarResponse>();

    this.realtimeService.eventChanged$.subscribe((change) => {
      this.queueChangeToast(change.changeType);

      if (change.changeType === EventChangeType.Removed) {
        this.removeFromCurrentList(change.eventId);
        window.localStorage.removeItem(environment.bayManagerUser);

        this.eventWorkerService.terminateEvent(
          new UpcomingEvents({ id: change.eventId }),
        );
        this.eventWorkerService.resetWorker();

        this.transaction.next(
          new Transaction({
            eventId: change.eventId,
            transactionType: TransactionType.EventDelete,
            simulatorIdentifier: change.simulatorIdentifier,
          }),
        );

        return;
      }

      this.eventService
        .getItemById(change.eventId, this.urlBase)
        .pipe(retry({ count: 3, delay: 2000 }))
        .subscribe((response) => {
          if (!response) return;

          window.localStorage.removeItem(environment.bayManagerUser);
          response.endDateTime = getUTCDateFromOffset(response.endDateTime);
          response.startDateTime = getUTCDateFromOffset(response.startDateTime);

          const isCreate = change.changeType === EventChangeType.Created;
          if (isCreate) {
            this.eventService.pushInUpcoming(response);
          } else {
            this.eventService.editFromUpcoming(response);
          }
          this.eventWorkerService.resetWorker();

          this.transaction.next(
            new Transaction({
              eventId: change.eventId,
              transactionType: isCreate
                ? TransactionType.EventCreate
                : TransactionType.EventUpdate,
              simulatorIdentifier: response.simulatorIdentifier,
              simulatorId: response.simulatorId,
              locationId: response.location?.id,
            }),
          );
        });
    });

    // this._subscriptions.add();

    // this._subscriptions.add();
  }

  private queueChangeToast(changeType: EventChangeType): void {
    if (changeType === EventChangeType.Created) {
      this.pendingChanges.created++;
    } else if (changeType === EventChangeType.Removed) {
      this.pendingChanges.removed++;
    } else {
      this.pendingChanges.updated++;
    }

    clearTimeout(this.updatedToastTimer);
    this.updatedToastTimer = setTimeout(() => {
      const c = this.pendingChanges;
      this.pendingChanges = { created: 0, updated: 0, removed: 0 };

      const parts: string[] = [];
      if (c.created) {
        parts.push(
          `${c.created} ${this.translate.instant(
            c.created === 1 ? 'eventCreated' : 'eventsCreated',
          )}`,
        );
      }
      if (c.updated) {
        parts.push(
          `${c.updated} ${this.translate.instant(
            c.updated === 1 ? 'eventUpdated' : 'eventsUpdated',
          )}`,
        );
      }
      if (c.removed) {
        parts.push(
          `${c.removed} ${this.translate.instant(
            c.removed === 1 ? 'eventRemoved' : 'eventsRemoved',
          )}`,
        );
      }

      if (parts.length) {
        this.toastService.info(
          `${this.translate.instant('calendarUpdated')} — ${parts.join(', ')}`,
        );
      }
    }, 1500);
  }

  public get urlBase(): string {
    return urlBase;
  }

  public async loadAllLocationsAndSimulators(): Promise<SimulatorResponse[]> {
    try {
      this.loaderService.loader = true;
      let simulators = new Array<SimulatorResponse>();

      await this.realtimeService.refreshOnlineSimulators();

      const locations = await lastValueFrom(
        this.simulatorService.getLocationsAndSimulatorsSelection(
          this.restApiService.user?.company?.id,
        ),
      );

      localStorage.setItem('locationsandsims', JSON.stringify(locations));
      if (locations && locations.length) {
        locations.forEach((location) => {
          location?.simulators?.forEach((simulator) => {
            let response = new SimulatorResponse({
              id: simulator.id,
              name: simulator.name,
              simulatorIdentifier: simulator.simulatorIdentifier,
            });

            response.isOnline = this.realtimeService.isSimulatorOnline(
              simulator.simulatorIdentifier,
            );

            simulators.push(response);
          });
        });
      }

      return simulators;
    } catch (error) {
      console.log(error);
      return new Array<SimulatorResponse>();
    } finally {
      this.loaderService.loader = false;
    }
  }

  public async onEditEvent(event: EventResponse): Promise<EventResponse> {
    try {
      const result = await this.eventService.updateEventGlobal(event);

      this.eventService.isSessionActive = false;

      if (result) {
        return event;
      }

      return null as any;
    } catch (error) {
      this.toastService.error(
        `Event ${event.name} was not saved due to some error`,
      );
      return null as any;
    } finally {
      this.eventService.isSessionActive = this.loaderService.loader = false;
    }
  }

  public async onOpenEventModal(
    data: EventModalObjects,
  ): Promise<EventModalResult> {
    try {
      if (!data.walkIn && !data.id && !data.selectInfo) {
        let selectInfo = {
          start: new Date(this.currentDateSelected), // + "T00:00:00",
          end: new Date(2024, 1, 1), // + "T00:00:00"
        };

        let newDate = new Date();
        selectInfo.start.setHours(newDate.getHours());
        selectInfo.start.setMinutes(newDate.getMinutes());
        selectInfo.start.setSeconds(newDate.getSeconds());

        data.selectInfo = selectInfo;
      }

      const dialogRef = this.dialog.open(EventModalComponent, {
        width: '600px',
        backdropClass: 'background-blur',
        data: new EventModalObjects({ ...data }),
      });

      const result: EventModalResult = await lastValueFrom(
        dialogRef.afterClosed(),
      );

      if (result) {
        if (result.mode === EventModalMode.create && result.event) {
          const newEvent = result.event;
          this.loaderService.loader = true;

          const resultCreated =
            await this.eventService.createEventGlobal(newEvent);

          this.loaderService.loader = false;
          if (!resultCreated || resultCreated.id <= 0) return null as any;

          newEvent.id = resultCreated.id;
          this.eventWorkerService.resetWorker();

          return new EventModalResult({
            event: newEvent,
            mode: result.mode,
            fromLocation: result.fromLocation,
            fromSimulator: result.fromSimulator,
          });
        } else if (result.mode === EventModalMode.changed && result.event) {
          const editEvent = result.event;
          editEvent.location = result.fromLocation;

          const response = await this.onEditEvent(editEvent);
          this.editFromCurrentList(response);

          this.eventWorkerService.resetWorker();

          return new EventModalResult({
            event: response,
            mode: result.mode,
            fromLocation: result.fromLocation,
            fromSimulator: result.fromSimulator,
          });
        } else if (result.mode === EventModalMode.delete && result.event) {
          await this.eventService.removeEventGlobal(result.event, true);
          this.removeFromCurrentList(result.event.id);

          this.eventWorkerService.resetWorker();

          return new EventModalResult({
            event: result.event,
            mode: result.mode,
            fromLocation: result.fromLocation,
            fromSimulator: result.fromSimulator,
          });
        }
      }

      return null as any;
    } catch (error) {
      console.log(error);
      return null as any;
    }
  }

  public pushInCurrentList(event: EventResponse): void {
    this._currentEvents = this._currentEvents.filter((f) => f.id !== event.id);
    this._currentEvents.push(new EventResponse({ ...event }));
    this._currentEvents.sort((a, b) => {
      if (
        getUTCDateFromOffset(a.startDateTime) >
        getUTCDateFromOffset(b.startDateTime)
      ) {
        return 1;
      } else if (
        getUTCDateFromOffset(a.startDateTime) <
        getUTCDateFromOffset(b.startDateTime)
      ) {
        return -1;
      }
      return 0;
    });
  }

  public editFromCurrentList(event: EventResponse): void {
    for (let j = 0; j < this._currentEvents.length; j++) {
      if (this._currentEvents[j].id === event.id) {
        this._currentEvents[j].endDateTime = event.endDateTime;
        this._currentEvents[j].startDateTime = event.startDateTime;
        this._currentEvents[j].locationName = event.locationName;
        this._currentEvents[j].name = event.name;
        this._currentEvents[j].simulatorId = event.simulatorId;
        this._currentEvents[j].simulatorName = event.simulatorName;
      }
    }
  }

  public findFromCurrentList(eventId: number): EventResponse {
    return this.currentEvents?.find((f) => f.id === eventId);
  }

  public extendMinuteCurrentList(event: ExtendSession): void {
    let extendedEvent = this._currentEvents.find((f) => f.id === event.eventId);

    if (extendedEvent) {
      extendedEvent.endDateTime.setMinutes(
        extendedEvent.endDateTime.getMinutes() + event.minutes,
      );
    }
  }

  public removeFromCurrentList(eventId: number): void {
    this._currentEvents = this.helper.removeElement(
      this._currentEvents,
      eventId,
    );
  }

  public getDuration(first: Date, second: Date): string {
    return getDurationByHours(second, first);
  }
}
